import React, { useEffect, useState, useContext } from 'react';
import { Popover } from '@headlessui/react';
import { XCircleIcon, CheckCircleIcon } from '@heroicons/react/20/solid';
import { PaperClipIcon } from '@heroicons/react/24/outline';
import moment from "moment"
import Footer from '../../components/footer';
import { AuthContext } from '../../contexts/auth';
import { Link } from 'wouter';
import Fetching from "../../components/fetching";
import { getAlerts, setAllAlertsRead } from '../../services/api_dipendenti';
import Empty from '../../components/empty';
import { useLocation } from 'wouter';
import Swal from 'sweetalert2';
import PrettyPagination from "../../components/pretty_pagination";
import { Tab } from '@headlessui/react';
import TextsmsIcon from '@mui/icons-material/Textsms';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import CustomTooltipComponent from '../../components/custom_tooltip_component';

export default function AvvisiComponent() {
  const { token } = useContext(AuthContext);
  const [isLoading, setIsLoading] = useState(false);
  const [isError, setIsError] = useState(false);
  const [alerts, setAlerts] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [unreadNotificationCount, setUnreadNotificationCount] = useState(0);
  const [unreadComunicationCount, setUnreadComunicationCount] = useState(0);
  const [alertsPage, setAlertsPage] = useState(1);
  const [alertsTotalPages, setAlertsTotalPages] = useState(1);
  const [notificationsPage, setNotificationsPage] = useState(1);
  const [notificationsTotalPages, setNotificationsTotalPages] = useState(1);
  const [selectedType, setSelectedType] = useState('Comunicazioni');
  const [location, setLocation] = useLocation();
  const [alertsTotalCount, setAlertsTotalCount] = useState(0);
  const [notificationsTotalCount, setNotificationsTotalCount] = useState(0);
  const [tabIndex, setTabIndex] = useState(0);

  const typeToTabIndex = (type) => (type === "Notifiche" ? 1 : 0);
  const tabIndexToType = (idx) => (idx === 1 ? "Notifiche" : "Comunicazioni");

  useEffect(() => {
    const queryParams = new URLSearchParams(window.location.search);
    const type = queryParams.get("type");

    const normalized = type === "Notifiche" ? "Notifiche" : "Comunicazioni";

    setSelectedType(normalized);
    setTabIndex(typeToTabIndex(normalized));
  }, [location]);


  useEffect(() => {
    if (token) {
      if (selectedType === 'Comunicazioni') {
        fetchAlerts(alertsPage);
      } else if (selectedType === 'Notifiche') {
        fetchNotifications(notificationsPage);
      }
    }
  }, [token, selectedType, alertsPage, notificationsPage]);

  const fetchAlerts = async (page) => {
    setIsLoading(true);
    setIsError(false);
    try {
      const response = await getAlerts(token, { page, page_size: 10 }, 'comunication');
      setAlerts(response.results);
      setUnreadComunicationCount(Number(response.unread_comunication_count ?? 0));
      setAlertsTotalCount(Number(response.count ?? 0));
      setAlertsTotalPages(Math.ceil((Number(response.count ?? 0)) / 10));
    } catch (error) {
      console.error('Error fetching alerts:', error);
      setIsError(true);
      Swal.fire({
        icon: 'error',
        title: 'Errore!',
        text: 'Si è verificato un errore durante il recupero delle comunicazioni.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const fetchNotifications = async (page) => {
    setIsLoading(true);
    setIsError(false);
    try {
      const response = await getAlerts(token, { page, page_size: 10 }, 'notification');
      setNotifications(response.results);
      setUnreadNotificationCount(Number(response.unread_notification_count ?? 0));
      setNotificationsTotalCount(Number(response.count ?? 0));
      setNotificationsTotalPages(Math.ceil((Number(response.count ?? 0)) / 10));
    } catch (error) {
      console.error('Error fetching notifications:', error);
      setIsError(true);
      Swal.fire({
        icon: 'error',
        title: 'Errore!',
        text: 'Si è verificato un errore durante il recupero delle notifiche.',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleReadAll = async () => {
    const category = selectedType === "Comunicazioni" ? "comunication" : "notification";

    try {
      const { isConfirmed } = await Swal.fire({
        icon: "question",
        title: "Confermi?",
        text: `Vuoi segnare tutte le ${selectedType.toLowerCase()} come lette?`,
        showCancelButton: true,
        confirmButtonText: "Sì",
        cancelButtonText: "Annulla",
        reverseButtons: true,
      });
      if (!isConfirmed) return;

      setIsLoading(true);
      await setAllAlertsRead(token, category);

      if (category === "comunication") {
        setAlerts((prev) => prev.map((a) => ({ ...a, is_read: true })));
      } else {
        setNotifications((prev) => prev.map((n) => ({ ...n, is_read: true })));
      }
      if (category === "comunication") {
        await fetchAlerts(alertsPage);
      } else {
        await fetchNotifications(notificationsPage);
      }

      Swal.fire({ icon: "success", title: "Salvato!", timer: 900, showConfirmButton: false });
    } catch (error) {
      console.error("Error marking all as read:", error);
      Swal.fire({
        icon: "error",
        title: "Errore!",
        text: "Non è stato possibile segnare tutti gli avvisi come letti.",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoading) {
    return <Fetching />;
  }

  return (
    <div className='flex flex-col min-h-screen'>
      <div className='flex-grow'>
        <Popover as="header" className={`bg-indigo-200`}>
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8">
            <div className="relative flex flex-wrap items-center justify-center lg:justify-between">
              <div className="w-full -5 lg:border-t lg:border-white lg:border-opacity-20">
                <div className="flex justify-center my-2 py-2">
                  <h2 className="text-center text-2xl font-semibold text-indigo-800">I miei avvisi</h2>
                </div>
              </div>
            </div>
          </div>
        </Popover>

        {isError && (
          <div className="px-4 sm:px-6 lg:px-8 mt-5">
            <p className="text-red-500">Si è verificato un errore durante il caricamento degli avvisi.</p>
          </div>
        )}
        <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8 pt-4">
          <Tab.Group
            selectedIndex={tabIndex}
            onChange={(idx) => {
              setTabIndex(idx);

              const newType = tabIndexToType(idx);
              setSelectedType(newType);
              
              if (newType === "Comunicazioni") setAlertsPage(1);
              else setNotificationsPage(1);

              setLocation(`/areariservata/avvisi?type=${newType}`);
            }}
          >
            <Tab.List className="flex gap-2 rounded-md bg-indigo-200/60 p-1 w-fit">
              <Tab className={({ selected }) =>
                `px-4 py-2 w-40 text-sm font-semibold rounded-md focus:outline-none ${
                  selected ? "bg-white text-indigo-700 shadow" : "text-indigo-900/80 hover:bg-white/60"
                }`
              }>
                <TextsmsIcon className='h-4 pe-2' />
                Comunicazioni
              </Tab>

              <Tab className={({ selected }) =>
                `px-4 py-2 w-40 text-sm font-semibold rounded-md focus:outline-none ${
                  selected ? "bg-white text-indigo-700 shadow" : "text-indigo-900/80 hover:bg-white/60"
                }`
              }>
                <NotificationsActiveIcon className='h-4 pe-2' />
                Notifiche
              </Tab>
            </Tab.List>
          </Tab.Group>
        </div>

        {!isError && selectedType === 'Comunicazioni' && alerts.length > 0 && (
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8 pt-4">
            <div className="mt-8 ring-1 ring-gray-300 sm:mx-0 rounded-lg overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th scope="col" className="w-10 py-3.5 pl-4 pr-1 sm:pl-6">
                      <span className="sr-only">Allegati</span>
                    </th>
                    <th scope="col" className="py-3.5 pl-2 pr-3 text-left text-sm font-semibold text-gray-900">
                      Titolo
                    </th>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Data
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Letto
                    </th>
                    {unreadComunicationCount > 0 ? (
                      <th scope="col" className="grid py-3.5 pl-3 pr-4 sm:pr-6 items-end flex-end justify-end">
                        <button 
                          onClick={handleReadAll}
                          className="
                            inline-flex items-center gap-2 rounded-full
                            border border-indigo-300 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700
                            shadow-sm hover:bg-indigo-50
                            focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:ring-offset-2
                            disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed
                            transition
                          "
                        >
                          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-indigo-100 text-indigo-700">
                            ✓
                          </span>
                          <span>Leggi tutto</span>
                          <span className="ml-1 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-bold text-indigo-700">
                            {unreadComunicationCount}
                          </span>
                        </button>
                      </th>
                    ) : (
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6"></th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {alerts.map((alert) => (
                    <tr key={alert.id}>
                      <td className="w-10 py-4 pl-4 pr-1 text-sm sm:pl-6">
                        {alert.attachments && Array.isArray(alert.attachments) && alert.attachments.length > 0 && (
                          <CustomTooltipComponent title="Sono presenti allegati" placement="top-start">
                            <div className="flex items-center">
                              <PaperClipIcon className="w-5 h-5 text-indigo-600" />
                            </div>
                          </CustomTooltipComponent>
                        )}
                      </td>
                      <td className="relative py-4 pl-2 pr-3 text-sm">
                        <div className="font-medium text-gray-900">{alert.title}</div>
                      </td>
                      <td className="px-3 py-3.5 text-sm text-gray-500 lg:table-cell">
                        <time dateTime={alert.created_at}>{moment(alert.created_at).format('DD/MM/YYYY HH:mm')}</time>
                      </td>
                      <td className="px-3 py-3.5 text-sm text-gray-500 lg:table-cell">
                        {alert.is_read ? (
                          <CheckCircleIcon className="w-5 h-5 text-teal-500 mr-1" />
                        ) : (
                          <XCircleIcon className="w-5 h-5 text-red-500 mr-1" />
                        )}
                      </td>
                      <td className="relative py-3.5 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        <Link to={`/areariservata/avvisi/${alert.id}/?type=${selectedType}`} className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white">
                          Leggi
                        </Link>
                        <div className="absolute -top-px left-0 right-6 h-px bg-gray-200" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <PrettyPagination
              page={alertsPage}
              totalPages={alertsTotalPages}
              totalCount={alertsTotalCount}
              onChange={(value) => setAlertsPage(value)}
              labelSingolare="comunicazione"
              labelPlurale="comunicazioni"
              className="py-3 border border-gray-200 bg-gray-50 mt-[-5px] rounded-b-md"
            />
          </div>
        )}

        {!isError && selectedType === 'Comunicazioni' && alerts.length === 0 && (<Empty />)}

        {!isError && selectedType === 'Notifiche' && notifications.length > 0 && (
          <div className="mx-auto max-w-3xl px-4 sm:px-6 lg:max-w-7xl lg:px-8 pt-4">
            <div className="mt-10 ring-1 ring-gray-300 sm:mx-0 rounded-lg overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-300">
                <thead>
                  <tr>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Titolo
                    </th>
                    <th scope="col" className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6">
                      Data
                    </th>
                    <th scope="col" className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900">
                      Letto
                    </th>
                    {unreadNotificationCount > 0 ? (
                      <th scope="col" className="grid py-3.5 pl-3 pr-4 sm:pr-6 items-end flex-end justify-end">
                        <button 
                          onClick={handleReadAll}
                          className="
                            inline-flex items-center gap-2 rounded-md
                            border border-indigo-300 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700
                            shadow-sm hover:bg-indigo-50
                            focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:ring-offset-2
                            disabled:border-gray-200 disabled:text-gray-400 disabled:cursor-not-allowed
                            transition
                          "
                        >
                          <span className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-indigo-100 text-indigo-700">
                            ✓
                          </span>
                          <span>Leggi tutto</span>
                          <span className="ml-1 inline-flex items-center rounded-full bg-indigo-100 px-2 py-0.5 text-[11px] font-bold text-indigo-700">
                            {unreadNotificationCount}
                          </span>
                        </button>
                      </th>
                    ) : (
                      <th scope="col" className="relative py-3.5 pl-3 pr-4 sm:pr-6"></th>
                    )}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {notifications.map((notification) => (
                    <tr key={notification.id}>
                      <td className="relative py-4 pl-4 pr-3 text-sm sm:pl-6">
                        <div className="font-medium text-gray-900">{notification.title}</div>
                      </td>
                      <td className="px-3 py-3.5 text-sm text-gray-500 lg:table-cell">
                        <time dateTime={notification.created_at}>{moment(notification.created_at).format('DD/MM/YYYY HH:mm')}</time>
                      </td>
                      <td className="px-3 py-3.5 text-sm text-gray-500 lg:table-cell">
                        {notification.is_read ? (
                          <CheckCircleIcon className="w-5 h-5 text-teal-500 mr-1" />
                        ) : (
                          <XCircleIcon className="w-5 h-5 text-red-500 mr-1" />
                        )}
                      </td>
                      <td className="relative py-3.5 pl-3 pr-4 text-right text-sm font-medium sm:pr-6">
                        <Link to={`/areariservata/avvisi/${notification.id}/?type=${selectedType}`} className="inline-flex items-center rounded-md bg-white px-2.5 py-1.5 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-30 disabled:hover:bg-white">
                          Leggi
                        </Link>
                        <div className="absolute -top-px left-0 right-6 h-px bg-gray-200" />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            
            <PrettyPagination
              page={notificationsPage}
              totalPages={notificationsTotalPages}
              totalCount={notificationsTotalCount}
              onChange={(value) => setNotificationsPage(value)}
              labelSingolare="notifica"
              labelPlurale="notifiche"
              className="py-3 border border-gray-200 bg-gray-50 mt-[-5px] rounded-b-md"
            />
          </div>
        )}
        
        {!isError && selectedType === 'Notifiche' && notifications.length === 0 && (<Empty />)}
      </div>
      <div className='pt-8'>
        <Footer />
      </div>
    </div>
  );
}